import { useEffect } from "react";
import { Alert, Card, Empty, Input, Spin, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { SearchHit } from "../components/SearchHit";
import { useUser } from "../context/UserContext";
import { hasAnyGrant } from "../helpers/permissions";
import { searchTarget } from "../helpers/searchTargets";
import { useGlobalSearch, type PaletteHit } from "../helpers/useGlobalSearch";

/** How many rows per group here — the palette shows 5, this is where the rest live. */
const PER_KIND = 20;

/**
 * The full result set behind the palette, for a query too broad to fit in a dropdown.
 * Reached by pressing Enter with nothing highlighted, so the term is always in the URL —
 * which also makes a search shareable and survives a refresh.
 */
export default function SearchResultsPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const { user } = useUser();
  const { term, setTerm, groups, loading, truncated, fuzzy } = useGlobalSearch(user, PER_KIND);

  // The URL is the source of truth: arriving from the palette, following a link or hitting
  // back all set the term the same way.
  useEffect(() => setTerm(query), [query, setTerm]);

  function pathFor(hit: PaletteHit): string | null {
    if (hit.source === "local") return hit.path;
    const target = searchTarget(hit.hit);
    return target && hasAnyGrant(user, target.permission) ? target.path : null;
  }

  return (
    <>
      <PageHeader title="Search" subtitle={query ? `Results for "${query}"` : undefined} />

      <Input
        size="large"
        prefix={<SearchOutlined />}
        suffix={loading ? <Spin size="small" /> : null}
        placeholder="Search pages, documents, items..."
        allowClear
        defaultValue={query}
        onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })}
        style={{ maxWidth: 520, marginBottom: 16 }}
      />

      {fuzzy && (
        <Alert
          type="warning"
          showIcon
          message={`Nothing matches "${term.trim()}" exactly - showing records with similar names.`}
          style={{ marginBottom: 16 }}
        />
      )}

      {truncated && (
        <Alert
          type="info"
          showIcon
          message="Showing the strongest matches only — narrow the term to see the rest."
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && term.trim() && groups.length === 0 && (
        <Empty description={`Nothing matches "${term.trim()}"`} />
      )}

      {groups.map((group) => (
        <Card key={group.key} title={group.label} size="small" style={{ marginBottom: 16 }}>
          {group.hits.map((hit) => {
            const path = pathFor(hit);
            const row = <SearchHit hit={hit} term={term} />;
            return (
              <div key={hit.key} className="glut-search-row glut-search-row-static">
                {/* A hit the user cannot open stays plain text rather than linking into a
                    403 — the same fallback DocLink makes. */}
                {path ? <Link to={path}>{row}</Link> : row}
              </div>
            );
          })}
        </Card>
      ))}

      {!query && (
        <Typography.Text type="secondary">
          Type above, or press <kbd>/</kbd> anywhere in the app.
        </Typography.Text>
      )}
    </>
  );
}
